import { useEffect } from 'react';

import api from '../../services/apiServices';
import { storage } from './storage';

// 로그인 토큰 수명은 2일인데 갱신 수단이 없었다.
// GT 페이지는 주기 요청이 하나도 없어서 만료를 아무도 감지하지 못하고,
// 만료 후 첫 요청인 저장 버튼이 401을 받아 그대로 로그아웃됐다.
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
// 남은 수명이 이보다 적으면 갱신한다. 2일짜리 토큰이면 하루에 한 번꼴.
const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;

let inFlight: Promise<void> | null = null;

const getExpiryMs = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const renewTokenIfNeeded = (): Promise<void> => {
  const token = storage.getToken();
  if (!token) {
    return Promise.resolve();
  }

  const expiry = getExpiryMs(token);
  if (!expiry) {
    return Promise.resolve();
  }

  const remaining = expiry - Date.now();
  // 이미 만료된 토큰으로 갱신을 시도하면 401을 받아 로그아웃된다. 재로그인 말고는 방법이 없다.
  if (remaining <= 0 || remaining > RENEW_BEFORE_MS) {
    return Promise.resolve();
  }

  if (inFlight) {
    return inFlight;
  }

  inFlight = api.auth
    .refreshToken()
    .then((res) => {
      if (res?.token) {
        storage.setToken(res.token);
      }
    })
    .catch(() => {
      // 일시적 실패는 넘긴다. 다음 주기에 다시 시도한다.
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

export const useTokenRefresh = () => {
  useEffect(() => {
    renewTokenIfNeeded();

    const timer = setInterval(renewTokenIfNeeded, CHECK_INTERVAL_MS);
    // 절전이나 탭 비활성 상태에서는 타이머가 밀린다. 돌아올 때 한 번 더 본다.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        renewTokenIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
};
